/**
 * Matching Status Badge Component
 * Displays the real-time status of the AI matching process
 */

import React from 'react';

interface MatchingStatusBadgeProps {
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'unknown';
  /** Automatic retries already attempted after a match failure (see backend
   * MAX_AUTO_RETRY_ATTEMPTS). A pending match with retryCount > 0 already
   * failed once and is waiting to retry — distinct from a fresh, never
   * processed pending match, so it gets its own badge state. */
  retryCount?: number;
}

const Spinner: React.FC = () => (
  <svg className="w-3.5 h-3.5 animate-spin shrink-0" viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
  </svg>
);

const statusConfig = {
  pending: {
    text: 'Queued for Analysis',
    color: 'bg-yellow-50 text-yellow-700 border-yellow-200',
    showSpinner: true
  },
  retrying: {
    text: 'Retrying Analysis...',
    color: 'bg-orange-50 text-orange-700 border-orange-200',
    showSpinner: true
  },
  processing: {
    text: 'Processing Analysis...',
    color: 'bg-blue-50 text-blue-700 border-blue-200',
    showSpinner: true
  },
  failed: {
    text: 'Analysis Failed',
    color: 'bg-red-50 text-red-700 border-red-200',
    showSpinner: false
  },
  unknown: {
    text: 'Status Unknown',
    color: 'bg-gray-50 text-gray-700 border-gray-200',
    showSpinner: false
  }
};

const MatchingStatusBadge: React.FC<MatchingStatusBadgeProps> = ({ status, retryCount = 0 }) => {
  if (status === 'completed') {
    return null;
  }

  const effectiveStatus = status === 'pending' && retryCount > 0 ? 'retrying' : status;
  const config = statusConfig[effectiveStatus] || statusConfig.unknown;

  return (
    <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border ${config.color}`}>
      <span className="text-sm font-medium">{config.text}</span>
      {config.showSpinner && <Spinner />}
    </div>
  );
};

export default MatchingStatusBadge;
